import { X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import PlaidLink from '../../../../../../components/signup/customers/PlaidLink';
import { Application } from '../../../../../../types/dbTypes';
import { ApplicationStep, Entity } from '../../../../../../types/apiTypes';
import { useState } from 'react';
import { useOptimisticApplicationStep } from '../../../../../../hooks/useOptimisticApplicationStep';

interface PlaidLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  application_id: string;
  application: Application;
  userId: string;
  // THT-703: when the customer chooses "Submit Bank Statements", hand off straight to the upload
  // modal instead of closing to the dashboard card (avoids the generic-card flash on the way there).
  onAdvanceToDocumentUpload?: () => void;
}

function PlaidLinkModal({
  isOpen,
  onClose,
  application_id,
  application,
  userId,
  onAdvanceToDocumentUpload,
}: PlaidLinkModalProps) {
  const navigate = useNavigate();
  const optimisticSetApplicationStep = useOptimisticApplicationStep(userId);
  const [loading, setLoading] = useState<boolean>(true);
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    navigate('/dashboard/home');
  };

  const handleSetStep = (step: string) => {
    if (step === ApplicationStep.PlaidLink) return;
    // THT-703 statements path: hand straight off to the upload modal (the parent optimistically
    // updates the step and swaps modals) instead of dropping to the dashboard card.
    if (
      step === ApplicationStep.ManualDocumentUpload &&
      onAdvanceToDocumentUpload
    ) {
      onAdvanceToDocumentUpload();
      return;
    }
    // Otherwise: optimistically reflect the new step so the dashboard updates immediately instead
    // of flashing the stale Plaid card while onClose()'s background refetch is in flight.
    optimisticSetApplicationStep(step);
    onClose();
    // Optionally navigate to dashboard home or stay on current page
    navigate('/dashboard/home');
  };

  const handleSetLoading = (isLoading: boolean) => {
    setLoading(isLoading);
  };

  return (
    <div
      data-testid="plaid-link-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <div className="relative h-full w-full bg-white">
        {/* Close Button */}
        {!loading && (
          <button
            onClick={handleClose}
            className="absolute right-6 top-6 z-[210] flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-600 shadow-lg transition-all hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close and return to dashboard"
          >
            <X size={24} weight="bold" />
          </button>
        )}

        {/* Plaid Link Component */}
        <div className="h-full w-full overflow-y-auto">
          <PlaidLink
            currStep={ApplicationStep.PlaidLink}
            selectedRole={Entity.Customer}
            setStep={handleSetStep}
            application_id={application_id}
            application={application}
            loading={loading}
            setLoading={handleSetLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default PlaidLinkModal;
